import axios from "axios";
import { useTheme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import CardHeader from "@mui/material/CardHeader";
import ButtonBase from "@mui/material/ButtonBase";
import CustomPopover, { usePopover } from "src/components/custom-popover";
import Iconify from "src/components/iconify";
import MenuItem from "@mui/material/MenuItem";
// auth
import { useAuthContext } from "../../../auth/useAuthContext";
// redux
import { useSelector } from "../../../redux/store";
// components
import { useSettingsContext } from "../../../components/settings";
// sections
import { AppWidgetSummary } from "../../../sections/@dashboard/general/app";
import setSixAM from "src/utils/setTimeTo6Am";

// ----------------------------------------------------------------------

export default function DashboardOrdersPage({ phone, customer }) {
  const { user } = useAuthContext();

  const theme = useTheme();

  const popover = usePopover();

  const { themeStretch } = useSettingsContext();

  const { stats, isLoading } = useSelector((state) => state.stat);
  const series = [
    "All Time",
    "Today",
    "Yesterday",
    "Last 7 days",
    "Last 30 days",
  ];
  const [seriesData, setSeriesData] = useState("All Time");
  const [filterEndDate, setFilterEndDate] = useState(null);

  const [filterStartDate, setFilterStartDate] = useState(null);
  const [orderListData, setOrderListData] = useState([]);
  const dateObj = new Date(user.venue.resetTime);
  const hour = dateObj.getHours();
  const minutes = dateObj.getMinutes();

  useEffect(() => {
    if (!user?.venue?.resetTime) return;

    const currentDate = new Date();
    const endDate = new Date();
    const level = JSON.parse(sessionStorage.getItem("levelSelected"));

    const resetTime = new Date(user.venue.resetTime);
    const resetHour = resetTime.getHours();
    const resetMinute = resetTime.getMinutes();

    setSixAM(currentDate, resetHour, resetMinute);
    setSixAM(endDate, resetHour, resetMinute);

    let currentHour = new Date().getHours();
    let currentMinutes = new Date().getMinutes();

    if (seriesData === "All Time") {
      // Set start date to customer's createdAt date for all time
      setFilterStartDate(new Date('2023-01-01'));
      setFilterEndDate(new Date());
      return;
    }

    if (
      (currentHour >= 0 && currentHour < resetHour) ||
      (currentHour === resetHour && currentMinutes < resetMinute)
    ) {
      switch (seriesData) {
        case "Today":
          currentDate.setDate(currentDate.getDate() - 1);
          break;
        case "Yesterday":
          currentDate.setDate(currentDate.getDate() - 2);
          endDate.setDate(endDate.getDate() - 1);
          break;
        case "Last 7 days":
          currentDate.setDate(currentDate.getDate() - 7);
          break;
        case "Last 30 days":
          currentDate.setDate(currentDate.getDate() - 30);
          break;
        default:
          break;
      }
    } else {
      switch (seriesData) {
        case "Today":
          endDate.setDate(endDate.getDate() + 1);
          break;
        case "Yesterday":
          currentDate.setDate(currentDate.getDate() - 1);
          break;
        case "Last 7 days":
          currentDate.setDate(currentDate.getDate() - 6);
          endDate.setDate(endDate.getDate() + 1);
          break;
        case "Last 30 days":
          currentDate.setDate(currentDate.getDate() - 29);
          endDate.setDate(endDate.getDate() + 1);
          break;
        default:
          break;
      }
    }

    setFilterStartDate(currentDate);
    setFilterEndDate(endDate);
  }, [seriesData, user?.venue?.resetTime, customer.createdAt]);

  useEffect(() => {
    getStats(filterStartDate, filterEndDate);
  }, [filterStartDate, filterEndDate]);

  const formatTime = (time) => {
    const options = { hour: "2-digit", minute: "2-digit", hour12: true };
    return new Intl.DateTimeFormat("en-US", options).format(time);
  };

  const formatDate = (time) => {
    const options = { day: "numeric", month: "short", year: "numeric" };
    return new Intl.DateTimeFormat("en-US", options).format(time);
  };

  const getStats = async (startDate, endDate) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const getToken = sessionStorage.getItem("accessToken");
      const level = JSON.parse(sessionStorage.getItem("levelSelected"));
      const defaultStartDate = new Date();
      defaultStartDate.setHours(6, 0, 0, 0);
      const startDateToSend = startDate
        ? startDate.toISOString()
        : defaultStartDate.toISOString();
      const defaultEndDate = new Date();
      defaultEndDate.setHours(6, 0, 0, 0);
      defaultEndDate.setDate(defaultEndDate.getDate() + 1);
      const endDateToSend = endDate
        ? endDate.toISOString()
        : defaultEndDate.toISOString();
      const response = await axios.get(
        `${backendUrl}/venueCustomer/getStatsByPhoneNumber`,
        {
          headers: {
            Authorization: `Bearer ${getToken}`,
          },
          params: {
            venueId: user.venue.id,
            levelId: level.id,
            phone: phone,
            startDate: startDateToSend,
            endDate: endDateToSend,
          },
        }
      );

      const completedOrders = response.data;

      setOrderListData(completedOrders);
    } catch (error) {
      console.log(error.message);
    }
  };

  const handleChangeSeries = useCallback(
    (newValue) => {
      popover.onClose();
      setSeriesData(newValue);
    },
    [popover]
  );

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CardHeader
            action={
              <ButtonBase
                onClick={popover.onOpen}
                sx={{
                  pl: 1,
                  py: 0.5,
                  pr: 0.5,
                  borderRadius: 1,
                  typography: "subtitle2",
                  bgcolor: "background.neutral",
                }}
              >
                {seriesData}

                <Iconify
                  width={16}
                  icon={
                    popover.open
                      ? "eva:arrow-ios-upward-fill"
                      : "eva:arrow-ios-downward-fill"
                  }
                  sx={{ ml: 0.5 }}
                />
              </ButtonBase>
            }
          />
        </Grid>
        <Grid container spacing={2} sx={{ m: 1 }}>
          {orderListData && (
            <Grid item xs={12} sm={6} md={4}>
              <AppWidgetSummary
                title="Total Orders"
                total={orderListData?.ordersCount}
              />
            </Grid>
          )}

          {user?.venue?.configurations?.isReservation && orderListData && (
            <Grid item xs={12} sm={6} md={4}>
              <AppWidgetSummary
                title="Total Reservations"
                prefix=""
                total={orderListData?.reservationsCount}
              />
            </Grid>
          )}

          {(user?.venue?.configurations?.isDineIn ||
            user?.venue?.configurations?.isPickUp) &&
            orderListData && (
              <Grid item xs={12} sm={6} md={4}>
                <AppWidgetSummary
                  title="Total Reviews"
                  total={orderListData?.reviewsCount}
                  // prefix="Rs. "
                />
              </Grid>
            )}

          {orderListData && (
            <Grid item xs={12} sm={6} md={4}>
              <AppWidgetSummary
                title="Last Order Date"
                date={
                  orderListData?.lastOrder?.createdAt
                    ? formatDate(new Date(orderListData?.lastOrder?.createdAt))
                    : "N/A"
                }
              />
            </Grid>
          )}

          {orderListData && (
            <Grid item xs={12} sm={6} md={4}>
              <AppWidgetSummary
                title="Last Order Amount"
                total={orderListData?.lastOrder?.total}
                prefix="Rs. "
              />
            </Grid>
          )}

          {orderListData && (
            <Grid item xs={12} sm={6} md={4}>
              <AppWidgetSummary
                title="Total Spent"
                total={orderListData.totalAmountSpend}
                prefix="Rs. "
              />
            </Grid>
          )}

          {user?.venue?.configurations?.isReservation && orderListData && (
            <Grid item xs={12} sm={6} md={4}>
              <AppWidgetSummary
                title="Last Reservation Date"
                date={
                  orderListData?.lastReservation?.createdAt
                    ? formatDate(
                        new Date(orderListData.lastReservation.createdAt)
                      )
                    : "N/A"
                }
              />
            </Grid>
          )}

          {user?.venue?.configurations?.isReservation && orderListData && (
            <Grid item xs={12} sm={6} md={4}>
              <AppWidgetSummary
                title="Last Reservation Amount"
                total={orderListData?.lastReservation?.advancePayment?.amount}
                prefix="Rs. "
              />
            </Grid>
          )}

          {user?.venue?.configurations?.isReservation && orderListData && (
            <Grid item xs={12} sm={6} md={4}>
              <AppWidgetSummary
                title="Refunded Reservations"
                total={orderListData?.totalRefundedReservation}
              />
            </Grid>
          )}

          {user?.venue?.configurations?.isReservation && orderListData && (
            <Grid item xs={12} sm={6} md={4}>
              <AppWidgetSummary
                title="Total Refunded Amount"
                total={orderListData?.totalRefundedReservationsAnount}
                prefix="Rs. "
              />
            </Grid>
          )}

          {(user?.venue?.configurations?.isDineIn ||
            user?.venue?.configurations?.isPickUp) &&
            orderListData && (
              <Grid item xs={12} sm={6} md={4}>
                <AppWidgetSummary
                  title="Avg. Rating"
                  total={orderListData?.lastreview?.orderAvgRating}
                  // prefix="Rs. "
                />
              </Grid>
            )}
        </Grid>
      </Grid>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        sx={{ width: 120 }}
      >
        {series.map((option) => (
          <MenuItem
            key={option}
            sx={{ fontSize: "14px" }}
            selected={option === seriesData}
            onClick={() => handleChangeSeries(option)}
          >
            {option}
          </MenuItem>
        ))}
      </CustomPopover>
    </>
  );
}
